/**
 * What the diary has been like lately, reduced to one of six words.
 *
 * Never stored: a mood is a pure function of the diary that is already
 * synced, so the client and the server compute the identical answer from the
 * identical facts without a mood table to keep in sync or go stale on an
 * unsynced device. It is also never spoken directly — no line ever says
 * "I'm neglected" — it only ever changes which lines are eligible to be
 * picked. See PERSONA.md for the register each mood is written in.
 */

import type { GoalPhase } from './energy.js'
import { hashSeed, mulberry32 } from './sass.js'

export type MoodKey = 'plain' | 'neglected' | 'proud' | 'gloomy' | 'festive' | 'loyal'

/**
 * A diary that has gone quiet this many days running stops reading as "busy"
 * and starts reading as "avoided."
 */
export const NEGLECT_THRESHOLD_DAYS = 3

/**
 * How far the silence has gone, in three steps — because "three days" and
 * "three weeks" are not the same joke told louder.
 *
 * `noticed` is a raised eyebrow, `pointed` stops pretending it hasn't been
 * counting, and `resigned` is where PERSONA.md caps the escalation: theatrical
 * resignation, never scolding. There is no fourth step on purpose — an
 * abandoned diary that keeps getting angrier is just an app that nags, and
 * she is a chronicler, not a nag.
 */
export type NeglectTier = 'noticed' | 'pointed' | 'resigned'

export const NEGLECT_POINTED_DAYS = 7
export const NEGLECT_RESIGNED_DAYS = 14

export function neglectTier(daysSinceLastLog: number): NeglectTier {
  if (daysSinceLastLog >= NEGLECT_RESIGNED_DAYS) return 'resigned'
  if (daysSinceLastLog >= NEGLECT_POINTED_DAYS) return 'pointed'
  return 'noticed'
}

/** Over-budget days in a trailing week that make it a pattern, not a run. */
export const STRUGGLE_OVER_DAYS = 5

/** Weekly weight movement under this, while cutting, reads as a stall. */
export const CUT_STALL_KG_PER_WEEK = 0.15

export interface StruggleInputs {
  phase?: GoalPhase | null
  /** Over-budget days in the trailing 7. */
  overDaysInWeek: number
  /** kg/week trend; only consulted on a cut. */
  weightTrendKgWk?: number | null
  /** A streak that was alive recently and has just broken. */
  streakRecentlyBroken: boolean
}

/**
 * Loyal mode's trigger: an overage-heavy week, a cut that has stopped
 * moving, or a streak that just ended. Any one of the three is enough — this
 * is not a score, it is a smoke detector.
 */
export function isStruggling(inputs: StruggleInputs): boolean {
  if (inputs.overDaysInWeek >= STRUGGLE_OVER_DAYS) return true
  if (
    inputs.phase === 'cut' &&
    inputs.weightTrendKgWk != null &&
    Math.abs(inputs.weightTrendKgWk) < CUT_STALL_KG_PER_WEEK
  ) {
    return true
  }
  return inputs.streakRecentlyBroken
}

/**
 * A per-account, per-ISO-week coin that always lands the same way for that
 * week — so a mood that looks spontaneous is still the same answer if asked
 * twice, which matters once a mood starts being cached rather than
 * recomputed on every render. Roughly one week in three lands on something
 * other than plain, split evenly between the two directions — a person who is
 * in exactly the same mood every ordinary week isn't a person, and `plain`
 * still has the majority of weeks either way.
 */
export function weeklyWhim(userId: string, isoWeek: string): 'proud' | 'gloomy' | null {
  const roll = mulberry32(hashSeed(`${userId}:${isoWeek}`))()
  if (roll < 1 / 6) return 'proud'
  if (roll < 1 / 3) return 'gloomy'
  return null
}

export interface MoodInputs {
  /** Days since the diary last had an entry; 0 means logged today. */
  daysSinceLastLog: number
  /** A holiday, either resident's birthday, Katė's birthday, or an app anniversary. */
  isFestiveToday: boolean
  struggling: boolean
  /** She leans gloomy through November regardless of the weekly whim. */
  isNovember: boolean
  weeklyWhim: 'proud' | 'gloomy' | null
}

/**
 * Priority, highest first: a struggle overrides everything, including a
 * festive date — loyalty is not a mood that takes a day off. Silence beats
 * a calendar date next: neglect is about the relationship, not the day.
 * Festive beats the ambient autumn lean, which beats the coin toss.
 */
export function computeMood(inputs: MoodInputs): MoodKey {
  if (inputs.struggling) return 'loyal'
  if (inputs.daysSinceLastLog >= NEGLECT_THRESHOLD_DAYS) return 'neglected'
  if (inputs.isFestiveToday) return 'festive'
  if (inputs.isNovember) return 'gloomy'
  if (inputs.weeklyWhim != null) return inputs.weeklyWhim
  return 'plain'
}
